// UI strings for the frame of ShareGate's web app: header, sidebar, banners, dialogs and error pages shown around every page, plus the developer tools panel.
// Its readers are IT admins, and the employees asked to review the Microsoft 365 workspaces they own.
// Translate only the string values. Keep every key, every {{placeholder}} and every <tag>…</tag> exactly as written.
// "ShareGate", "ShareGate Protect" and Microsoft product names stay untranslated. Use a friendly, plain, non-technical tone.
export const en = {
    // Generic error page, shown when the app fails unexpectedly.
    InternalServerError: {
        title: "Something didn’t go as expected, but we’re on it!",
        description: "We’ll have everything back up and running for you shortly. Thanks for hanging in there with us.",
        // <supportLink> wraps the words for "contact support", which link to ShareGate's support page.
        contactSupport: "If we keep sending you back here, <supportLink>contact support</supportLink>.",
        // Button to ShareGate's home page.
        goHome: "Go to ShareGate Home"
    },
    // Error page shown when part of the app fails to download, usually because a new version was just released.
    ChunkLoadError: {
        title: "The screen you're looking for wasn't found",
        description:
            "It's likely the page was recently updated and the browser needs to be reloaded. You can reload now or go back to your previous page.",
        goBack: "Go Back",
        reload: "Reload"
    },
    // Error page shown for an address that doesn't exist.
    NotFoundError: {
        // Screen-reader-only label of the illustration.
        illustration: "Page not found",
        title: "Quick detour to get around this 404",
        // <homeLink> wraps the name of ShareGate's home page, which links to it.
        description:
            "Don’t worry, we’ll get you back to the main road. Head this way to <homeLink>ShareGate Home</homeLink>.",
        // <supportLink> wraps the words for "contact support", which link to ShareGate's support page.
        contactSupport: "If we keep sending you back here, <supportLink>contact support</supportLink>."
    },
    // Error page shown when the workspace's subscription doesn't include the product.
    ForbiddenError: {
        title: "Your subscription doesn’t include ShareGate Protect",
        // <supportLink> wraps the words for "contact support", which link to ShareGate's support page.
        contactSupport: "If this seems incorrect to you, please <supportLink>contact support</supportLink>.",
        manageSubscription: "Manage my subscription"
    },
    // Logo at the top of the error pages.
    ErrorLayout: {
        // Screen-reader-only label of the logo.
        logo: "ShareGate"
    },
    // Link at the very top of the page, visible only when reached with the keyboard, that jumps past the menus.
    SkipToMain: {
        label: "Skip to Content"
    },
    // Screen-reader-only labels of the spinners shown while the app or a page loads.
    Loaders: {
        loading: "Loading…",
        loadingPage: "Loading page…"
    },
    // Bar at the top of every page.
    Header: {
        // Screen-reader-only label of the button that opens the navigation menu on small screens.
        menu: "Menu",
        // Screen-reader-only label of the button that leaves a single-task page.
        close: "Close"
    },
    // Navigation menu on the left of every page.
    Sidebar: {
        // Screen-reader label and tooltip of the button that narrows the menu to icons only.
        collapse: "Collapse Sidebar",
        expand: "Expand Sidebar",
        archives: {
            label: "Archives (legacy)",
            tooltip: "Access your previously archived groups."
        }
    },
    // "Feedback" link at the bottom of the left navigation menu, and the menu it opens.
    FeedbackButton: {
        label: "Feedback",
        heading: "Share Feedback",
        interview: {
            label: "Talk to our product team",
            description: "Book a call to discuss IT challenges and help shape what we build."
        }
    },
    // Menu opened from the user's picture at the top right of every page.
    UserMenu: {
        // Screen-reader-only label of the user's picture.
        openMenu: "Open menu",
        logOut: "Log out",
        getHelp: "Get help",
        terms: "Terms & Conditions"
    },
    // Workspace name at the top right of every page, which opens the workspace switcher.
    WorkspaceMenu: {
        // Screen-reader-only. {{workspaceName}} is the name of the workspace the user is in.
        trigger: "Current workspace: {{workspaceName}}. Select to change workspace."
    },
    // Strings shared by the workspace switcher's menu and dialog.
    WorkspaceSwitcher: {
        // Title of the list of the user's own workspaces.
        yourWorkspaces_one: "Your workspace",
        yourWorkspaces_other: "Your workspaces",
        // Title of the list of workspaces of the customers the user manages.
        clientWorkspaces: "Client workspaces",
        newWorkspace: "New workspace"
    },
    // Menu listing the workspaces, for users with a few of them.
    WorkspaceSwitcherMenu: {
        workspaces: "Workspaces"
    },
    // Dialog listing the workspaces, for users with many of them.
    WorkspaceSwitcherModal: {
        title: "Switch workspace",
        // Shown while the search box is empty.
        count_one: "{{count}} workspace",
        count_other: "{{count}} workspaces",
        // Shown while searching. {{matchCount}} is how many workspaces match the search.
        matchCount_one: "{{matchCount}} of {{count}} workspace",
        matchCount_other: "{{matchCount}} of {{count}} workspaces",
        search: {
            label: "Search workspaces",
            placeholder: "Search workspaces…"
        },
        noResults: "No workspaces found",
        // Screen-reader-only labels of a workspace in the list. {{workspaceName}} is its name.
        currentWorkspace: "{{workspaceName}} (current workspace)",
        switchTo: "Switch to {{workspaceName}}",
        cancel: "Cancel"
    },
    // Tip that points at the workspace switcher right after the user switched workspaces.
    SwitcherTipPopover: {
        // {{workspaceName}} is the name of the workspace the user just switched to.
        title: "You're in {{workspaceName}}'s workspace",
        description: "Use this switcher to navigate between your own workspace and client workspaces.",
        dismiss: "Got it"
    },
    // Search box at the top of every page, and the list of results it opens.
    CommandPalette: {
        search: "Search",
        // {{entities}} is a list of what can be searched, such as "teams, groups or sites".
        searchEntities: "Search {{entities}}",
        // Screen-reader-only labels of the results list and of its AI section.
        results: "Search results",
        aiSection: "AI",
        // Joins the last item of a list to the others. {{items}} holds the other items, already separated by commas.
        list: {
            and: "{{items}} and {{last}}",
            or: "{{items}} or {{last}}"
        },
        status: {
            // Screen-reader-only label of the spinner.
            searching: "Searching",
            searchingEllipsis: "Searching…",
            // {{entities}} is a list of what can be searched, such as "teams, groups and sites".
            typeToSearch: "Start typing to search through {{entities}}.",
            noResults: "No {{entities}} found with that name. Check the spelling or try another."
        },
        generateAiReport: {
            label: "Generate report with AI",
            // Followed by the text the user typed.
            withPrompt: "Generate report with AI:"
        }
    },
    // Text inside the search box at the top of every page.
    CommandPaletteTrigger: {
        // {{query}} is the text the user typed.
        withQuery: "Search: {{query}}",
        // {{tenantName}} is the name of the user's Microsoft 365 tenant.
        inTenant: "Search in {{tenantName}}"
    },
    // Greeting at the top of the home page. {{firstName}} is the user's first name.
    HomeGreeting: {
        title: "Hi, {{firstName}}"
    },
    // Banner at the top of every page during the grace period that follows an expired subscription.
    GracePeriodBanner: {
        title: "Your subscription has expired",
        // Tooltip. {{count}} is the number of days data is kept.
        retention_one:
            "Your data is saved for {{count}} day after your subscription ends, then permanently deleted from ShareGate.",
        retention_other:
            "Your data is saved for {{count}} days after your subscription ends, then permanently deleted from ShareGate.",
        // <contactLink> wraps the words for "Contact sales", which open a chat with ShareGate's sales team.
        description:
            "Your data is no longer being updated but will remain accessible for a limited time. <contactLink>Contact sales</contactLink> to keep governing your tenant."
    },
    // Badge next to a page title during the grace period, saying when the data was last updated.
    LastScannedBadge: {
        today: "Last scanned today",
        daysAgo_one: "Last scanned {{count}} day ago",
        daysAgo_other: "Last scanned {{count}} days ago"
    },
    // Banner at the top of every page for workspaces on a read-only plan.
    ReadOnlyBanner: {
        // <contactLink> wraps the words for "contact us", which open a chat with ShareGate's sales team.
        description:
            "Unlock remediation actions and automated policies by upgrading your plan! Ask your ShareGate partner or <contactLink>contact us</contactLink>.",
        expiry: {
            expired: "Your plan has expired.",
            today: "Your plan expires today.",
            inDays_one: "Your plan expires in {{count}} day.",
            inDays_other: "Your plan expires in {{count}} days.",
            // {{date}} is a formatted date, such as "March 3, 2027".
            onDate: "Your plan expires on {{date}}."
        }
    },
    // Banner at the top of every page when a subscription is expiring or has expired.
    SubscriptionExpirationBanner: {
        status: {
            expiring: "One or more of your subscriptions is expiring soon.",
            "expired-single": "Your subscription has expired.",
            "expired-trial-single": "Your free trial has expired.",
            "expired-all": "Your subscriptions have expired.",
            "expired-some": "One or more of your subscriptions has expired."
        },
        // <strong> wraps the words shown in bold.
        trialToday: "Your free trial expires <strong>today</strong>.",
        trialDaysLeft_one: "<strong>{{count}} day left</strong> on your free trial.",
        trialDaysLeft_other: "<strong>{{count}} days left</strong> on your free trial.",
        contactReseller: "Contact your reseller for a quote",
        viewPlans: "View plans"
    },
    // Banner at the top of every page when the subscription is blocked for an unpaid invoice.
    BlockedSubscriptionBanner: {
        message: "Since payment has not yet been received in full, your subscription is currently blocked.",
        contactReseller: "Contact your reseller for assistance",
        contact: "Contact us"
    },
    // Dialog shown when the user opens a feature they have no access to.
    RestrictedAccessModal: {
        // <chatLink> wraps the word for "chat", which opens a chat with ShareGate's team.
        trialExpired: {
            heading: "Your free trial has ended",
            adminDescription:
                "We hope you enjoyed your 15-day trial experience. To continue using ShareGate, pick a subscription plan that fits your needs. Questions about pricing or plans? Reach out through <chatLink>chat</chatLink>!",
            description:
                "We hope you enjoyed your 15-day trial experience. To keep using ShareGate, ask your workspace admin to purchase a subscription."
        },
        subscriptionExpired: {
            heading: "Your subscription has ended",
            adminDescription:
                "We hope you enjoyed your experience! If you change your mind and want to keep using ShareGate, please reach out through <chatLink>chat</chatLink> so we can renew your subscription.",
            description:
                "We hope you enjoyed your experience! To keep using ShareGate, ask your workspace admin to renew your subscription."
        },
        noSubscription: {
            heading: "You don't have an active subscription for this feature",
            adminDescription: "Purchase a subscription plan that includes this feature before continuing.",
            description:
                "Ask your workspace admin to purchase a subscription plan that includes this feature before continuing."
        },
        noAccess: {
            requestAccessHeading: "Request product access",
            protectHeading: "You don't have access to ShareGate's governance features",
            migrateHeading: "You don't have access to ShareGate's migration features",
            heading: "You don't have access to this feature",
            adminDescription:
                "This is currently turned off for your account, but you can enable access for yourself from the Members page. If you need extra help, please contact us through <chatLink>chat</chatLink>.",
            protectDescription:
                "Your access to ShareGate's governance features is currently turned off. Ask a workspace admin to enable it for you.",
            migrateDescription:
                "Your access to ShareGate's migration features is currently turned off. Ask a workspace admin to enable it for you.",
            description:
                "Your access to this feature is currently turned off. Ask a workspace admin to enable it for you."
        },
        choosePlan: "Choose a plan",
        goToMembers: "Go to Members page",
        gotIt: "Got it",
        cancel: "Cancel"
    },
    // Message shown in place of a page or a section the user has no access to.
    RestrictedContent: {
        noAccess: {
            heading: "Request product access",
            pageMessage:
                "Your access to this page is currently turned off. Ask a workspace admin to enable it for you.",
            sectionMessage:
                "Your access to this section is currently turned off. Ask a workspace admin to enable it for you."
        },
        noAccessAdmin: {
            pageHeading: "You don't have permission to view this page",
            sectionHeading: "You don't have permission to view this section",
            message: "You can give yourself access directly from the Members page."
        },
        goToMembers: "Go to Members page"
    },
    // Names of the regions where a workspace's data can be stored.
    Regions: {
        na: "North America",
        eu: "Europe"
    },
    // Dropdown that switches the data region.
    RegionPicker: {
        // Screen-reader-only.
        label: "Select region"
    },
    // Dialog asking the user to pick a data region on their first visit.
    RegionSelectionModal: {
        title: "Select your assessment data region",
        // <learnMoreLink> wraps the words for "Learn more", which link to a help article.
        description:
            "This is the region where your assessment data will be stored. <learnMoreLink>Learn more</learnMoreLink>",
        // Screen-reader-only label of the list of regions.
        regionGroup: "Region selection",
        confirm: "Confirm"
    },
    // Dialog asking a new trial user what they will use ShareGate for.
    TrialReasonModal: {
        title: "Tell us how you'll use ShareGate",
        // {{firstName}} is the user's first name.
        greeting: "We'd like to craft the best experience for you, {{firstName}}!",
        question: "Tell us how you'll use ShareGate.",
        reasons: {
            onpremTo365: "On-premises to Microsoft 365 migrations",
            tenantToTenant: "Tenant-to-tenant migrations",
            tenantToTenantDetail: "(e.g., SharePoint, OneDrive, or Teams)",
            googleTo365: "Google Workspace to Microsoft 365 migrations",
            mailbox: "Exchange Online mailbox migrations",
            governance: "M365 Governance",
            entraId: "Identity migrations (Entra ID)"
        },
        done: "Done",
        submitError: "We couldn't save your answer. Please try again."
    },
    // Placeholder banner shown only in the component catalog, standing in for a banner added by a chat tool.
    InlineBanner: {
        label: "Inline banner"
    },
    // Developer-only tools panel at the bottom right of the page, never shown to customers.
    DevTools: {
        // Screen-reader-only labels of the button that opens and closes the panel.
        collapse: "Collapse DevTools panel",
        expand: "Expand DevTools panel"
    },
    // Developer-only color scheme menu in the tools panel.
    ColorSchemeToggleTool: {
        label: "Edit User preferences…",
        heading: "Color Scheme",
        light: "Light",
        dark: "Dark"
    },
    // Developer-only feature flag dialog in the tools panel.
    FeatureFlagPickerTool: {
        label: "Edit feature flags…",
        title: "Edit feature flags"
    },
    // Developer-only data inspector button in the tools panel.
    ReactQueryDevTool: {
        tooltip: "Open React Query Devtools"
    },
    // Developer-only button in the tools panel.
    ResetLocalStorageTool: {
        label: "Reset Local Storage"
    },
    // Developer-only dialog in the tools panel that switches the mock data scenarios.
    MswScenariosConfigurator: {
        title: "MSW Scenarios",
        resetAll: "Reset All",
        description:
            "Switch between mock scenarios at runtime without changing code. A page reload is required for changes to take effect.",
        hoverHint: "Hover over a scenario to see its description",
        // {{scenario}} is the name of another scenario.
        blockedBy: '(Disabled while "{{scenario}}" is enabled)',
        scoped: {
            // <strong> wraps {{workspaceName}}, the name of a workspace.
            workspace:
                "Some scenarios here only apply to <strong>{{workspaceName}}</strong> and are hidden. Switch to that workspace to change them.",
            // {{scope}} is a kind of workspace, such as "partner".
            scope: "Some scenarios here only apply to the {{scope}} workspace and are hidden. Switch to it to change them.",
            enabled_one: "{{count}} of them is enabled.",
            enabled_other: "{{count}} of them are enabled."
        },
        cancel: "Cancel",
        save: "Save & Reload"
    },
    // Developer-only sign-in page of the mock environment, never shown to customers.
    LoginPage: {
        title: "Login",
        note: "NOTE: This fake login page is strictly for development purpose when using an MSW environment with fake endpoints.",
        submit: "Login",
        error: "Login failed"
    },
    // Developer-only sign-out page of the mock environment, never shown to customers.
    LogoutPage: {
        title: "Logged out",
        note: "NOTE: This fake logout page is strictly for development purpose when using an MSW environment with fake endpoints.",
        description: "You are logged out from the application!",
        logIn: "Log in again"
    }
} as const;
